import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ModeToggle } from '@/components/mode-toggle';
import { LanguagePicker } from '@/components/language-picker';
import { useLocaleSync } from '@/hooks/useLocaleSync';
import { Button } from '@/components/ui/button';
import { RegisterPage } from '@/pages/RegisterPage';
import { LoginPage } from '@/pages/LoginPage';
import { FamiliesPage } from '@/pages/FamiliesPage';
import { FamilySettingsPage } from '@/pages/FamilySettingsPage';
import { InviteAcceptPage } from '@/pages/InviteAcceptPage';
import { TimelinePage } from '@/pages/TimelinePage';
import { PhotoViewerPage } from '@/pages/PhotoViewerPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { OnboardingWizard } from '@/pages/OnboardingWizard';
import { LogOut } from 'lucide-react';

// Marketing pages — lazy-loaded to keep authenticated app bundle lean
const MarketingLayout = lazy(() => import('@/components/marketing/MarketingLayout').then(m => ({ default: m.MarketingLayout })));
const HomePage = lazy(() => import('@/pages/marketing/HomePage').then(m => ({ default: m.HomePage })));
const FeaturesPage = lazy(() => import('@/pages/marketing/FeaturesPage').then(m => ({ default: m.FeaturesPage })));
const PricingPage = lazy(() => import('@/pages/marketing/PricingPage').then(m => ({ default: m.PricingPage })));
const PrivacyPage = lazy(() => import('@/pages/marketing/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const AboutPage = lazy(() => import('@/pages/marketing/AboutPage').then(m => ({ default: m.AboutPage })));

// Dev login page — tree-shaken in production builds
const DevLoginPage = import.meta.env.DEV
  ? lazy(() => import('@/pages/DevLoginPage').then(m => ({ default: m.DevLoginPage })))
  : null;

/**
 * AuthGate - Renders auth pages when unauthenticated, main app when authenticated.
 * Shows a loading spinner while the session is being checked.
 */
export function AuthGate() {
  const { isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div data-testid="auth-loading" className="flex min-h-screen items-center justify-center" role="status" aria-label="Loading">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary-600" />
          <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        {/* Marketing site — wrapped in shared nav/footer */}
        <Route element={<Suspense fallback={null}><MarketingLayout /></Suspense>}>
          <Route path="/" element={<HomePage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/about" element={<AboutPage />} />
        </Route>

        {/* Auth pages — standalone, no marketing layout */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        {DevLoginPage && (
          <Route path="/dev/login" element={<Suspense fallback={null}><DevLoginPage /></Suspense>} />
        )}

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Authenticated - show main app
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/register" element={<Navigate to="/" replace />} />
        <Route path="/photo/:id" element={<div className="animate-pageIn"><PhotoViewerPage /></div>} />
        <Route path="*" element={<MainApp />} />
      </Routes>
    </ErrorBoundary>
  );
}

/**
 * Main app shell shown after authentication.
 * Contains header and all family/timeline/album routes.
 */
function MainApp() {
  const { user, logout } = useAuth();
  const { persistLocale } = useLocaleSync(user);
  const location = useLocation();

  return (
    <div data-testid="main-app" className="min-h-screen">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-primary-600 focus:px-4 focus:py-2 focus:text-white">
        Skip to main content
      </a>
      <header className="bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5">
          <h1 className="flex items-center gap-2 font-mono text-sm uppercase tracking-widest text-foreground">
            <img src="/favicon.svg" alt="" className="h-5 w-5" />
            WovenDays
          </h1>
          <div className="flex items-center gap-3">
            <Link to="/profile" className="hidden text-xs font-mono text-muted-foreground sm:inline hover:text-foreground transition-colors">{user?.name}</Link>
            <LanguagePicker onLocaleChange={persistLocale} />
            <ModeToggle />
            <Button variant="ghost" size="sm" onClick={logout} className="text-muted-foreground">
              <LogOut className="mr-1.5 h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <main id="main-content" className="mx-auto max-w-7xl px-6 py-10">
        <ErrorBoundary>
          <div key={location.pathname} className="animate-pageIn">
            <Routes location={location}>
              <Route path="/families/:id/timeline" element={<TimelinePage />} />
              <Route path="/families/:id" element={<FamilySettingsPage />} />
              <Route path="/invite/:token" element={<InviteAcceptPage />} />
              <Route path="/onboarding" element={<OnboardingWizard />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="*" element={<FamiliesPage />} />
            </Routes>
          </div>
        </ErrorBoundary>
      </main>
    </div>
  );
}
